import { SidebarNavBreakpoint, ThreeColumnNavBreakpoint, WideSidebarNavBreakpoint } from "@gadgetinc/widgets/src/DocsTheme";
import { styled, useStyletron } from "baseui";
import { HeadingSmall } from "baseui/typography";
import React from "react";
import { DocsFeedback } from "../../components/DocsFeedback";
import type { ExtractedHeading } from "../../utils/extractMDXHeadings";
import { LargeSidebarWidth, SmallSidebarWidth } from "./DocsLeftNav";
import { useDocsLocation } from "./useDocsLocation";

const TableOfContentsItemBlob = styled<{ $active: boolean }, "div">("div", ({ $theme, $active }) => ({
  ...$theme.typography.LabelSmall,
  paddingLeft: $theme.sizing.scale300,
  paddingRight: $theme.sizing.scale300,
  paddingTop: $theme.sizing.scale200,
  paddingBottom: $theme.sizing.scale200,
  cursor: "pointer",
  backgroundColor: $active ? $theme.colors.primary100 : undefined,
  borderRadius: $theme.borders.radius200,
  ":hover": {
    backgroundColor: $theme.colors.primary50,
  },
}));

const TableOfContentsItemContainer = styled<{ $active: boolean }, "li">("li", ({ $theme, $active }) => ({
  borderLeftStyle: "solid",
  borderLeftColor: $active ? $theme.colors.accent500 : $theme.colors.primary200,
  borderLeftWidth: "1px",
  paddingLeft: $theme.sizing.scale200,
  listStyleType: "none",
}));

const TableOfContentsItemLink = styled<{ $active: boolean }, "a">("a", ({ $theme, $active }) => ({
  textDecoration: "none",
  color: $theme.colors.contentPrimary,
  fontWeight: $active ? 500 : 400,
  display: "inline-block",
}));

const TableOfContentsContainer = styled("aside", ({ $theme }) => ({
  // Show/hide the right side table of contents based on the screen size breakpoint
  display: "none",
  [`@media screen and ${ThreeColumnNavBreakpoint}`]: {
    display: "block",
    minWidth: SmallSidebarWidth,
    marginLeft: $theme.sizing.scale800,
  },
  [`@media screen and ${WideSidebarNavBreakpoint}`]: {
    width: LargeSidebarWidth,
    marginLeft: $theme.sizing.scale1200,
  },
}));

export const OnlyShowOnThreeColumnNav = styled("div", ({ $theme }) => ({
  [`@media screen and ${ThreeColumnNavBreakpoint}`]: {
    display: "none",
  },
}));

export const HideWhenSidebarVisible = styled("div", ({ $theme }) => ({
  display: "none",
  [`@media screen and ${SidebarNavBreakpoint}`]: {
    display: "unset",
  },
}));

const TableOfContentsStickyNav = styled("nav", ({ $theme }) => ({
  marginTop: $theme.sizing.scale1000,
  position: "sticky",
  top: "60px",
  maxHeight: "calc(100vh - 100px)",
  overflowY: "scroll",
  ["::-webkit-scrollbar"]: {
    width: $theme.sizing.scale200,
    height: $theme.sizing.scale200,
  },
  ["::-webkit-scrollbar-thumb"]: {
    background: "none",
  },
  ["::-webkit-scrollbar-track"]: {
    background: "none",
  },
  ["::-webkit-scrollbar-corner"]: {
    background: "none",
  },
  [":hover"]: {
    scrollbarColor: $theme.colors.borderFocus,
  },
  [":hover::-webkit-scrollbar-thumb"]: {
    backgroundColor: $theme.colors.borderFocus,
    border: "2px solid transparent",
    borderRadius: "10px",
    backgroundClip: "content-box",
  },
}));

const TableOfContentsItem = (props: { heading: ExtractedHeading }) => {
  const [location, setLocation] = useDocsLocation();
  const active = location.split("#", 2)[1] === props.heading.url.slice(1);

  return (
    <TableOfContentsItemContainer
      $active={active}
      onClick={() => {
        setLocation(props.heading.url);
      }}
    >
      <TableOfContentsItemBlob $active={active}>
        <TableOfContentsItemLink
          $active={active}
          href={props.heading.url}
          style={{
            paddingLeft: `${(props.heading.depth - 2) * 16}px`,
            width: "100%",
            textOverflow: "ellipsis",
            overflow: "hidden",
          }}
        >
          {props.heading.text}
        </TableOfContentsItemLink>
      </TableOfContentsItemBlob>
    </TableOfContentsItemContainer>
  );
};

export const DocsPageTableOfContents = (props: { headings: ExtractedHeading[] }) => {
  const [css, $theme] = useStyletron();

  return (
    <TableOfContentsContainer>
      <TableOfContentsStickyNav>
        <HeadingSmall
          $style={{
            position: "sticky",
            top: 0,
            paddingBottom: $theme.sizing.scale400,
            backgroundColor: $theme.colors.background,
          }}
        >
          On this page
        </HeadingSmall>
        <ul className={css({ marginTop: $theme.sizing.scale400, marginRight: $theme.sizing.scale200 })}>
          {props.headings.map((heading) => (
            <TableOfContentsItem key={heading.url} heading={heading} />
          ))}
        </ul>
        <div style={{ paddingTop: $theme.sizing.scale800, paddingLeft: $theme.sizing.scale400 }}>
          <DocsFeedback />
        </div>
      </TableOfContentsStickyNav>
    </TableOfContentsContainer>
  );
};
